import { useEffect, useMemo, useState } from "react";
import Header from "../components/Header";
import MobileMenu from "../components/MobileMenu";
import Overlay from "../components/Overlay";
import Footer from "../components/Footer";
import { useNavigate } from "react-router-dom";
import { User, Medal } from "../type";
import { useAuth } from "../contexts/AuthContext";
import api from "../services/api";
import { ChevronDown } from "lucide-react";

type ActiveTab = "coaches" | "athletes";
type SortKey = "program_count" | "distance_km" | "elevation_m" | "badge_count";

const sortOptions: { label: string; value: SortKey }[] = [
    { label: "پیمایش", value: "distance_km" },
    { label: "ارتفاع", value: "elevation_m" },
    { label: "تعداد برنامه", value: "program_count" },
    { label: "تعداد مدال", value: "badge_count" },
];

const LeaderboardPage = () => {
    const { isAuthenticated, accessToken, loading: loadingauth } = useAuth();
    const [activeTab, setActiveTab] = useState<ActiveTab>("athletes");
    const navigate = useNavigate();

    const [loading, setLoading] = useState(true);
    const [menuOpen, setMenuOpen] = useState(false);
    const [activeTabFooter, setActiveTabFooter] = useState("leaderboard");
    const [users, setUsers] = useState<User[]>();
    const [sortBy, setSortBy] = useState<SortKey>("program_count");
    const [dropdownOpen, setDropdownOpen] = useState(false);

    const fetchdata = async () => {
        try {
            const mainRes = await api.get("/leaderboard", {
                headers: {
                    Authorization: accessToken ? `Bearer ${accessToken}` : "",
                },
            });
            if (mainRes.data?.data) {
                setUsers(mainRes.data.data);
            }
        } catch (err) {
            console.log("data fetch error:", err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (loadingauth) return;
        if (!isAuthenticated) {
            navigate("/");
            return;
        }
        fetchdata();
    }, [isAuthenticated, accessToken, loadingauth]);

    const sortUsers = (list: User[]) =>
        [...list].sort((a, b) => {
            const aVal = parseFloat(String(a[sortBy] ?? 0));
            const bVal = parseFloat(String(b[sortBy] ?? 0));
            return bVal - aVal;
        });

    const coaches = useMemo(
        () => users ? sortUsers(users.filter((u) => u.role === "coach")) : [],
        [users, sortBy]
    );

    const athletes = useMemo(
        () => users ? sortUsers(users.filter((u) => u.role === "athlete")) : [],
        [users, sortBy]
    );

    const currentList = useMemo(
        () => (activeTab === "coaches" ? coaches : athletes),
        [activeTab, coaches, athletes]
    );

    const activeSortLabel = sortOptions.find((o) => o.value === sortBy)?.label;

    return (
        <div dir="rtl" className="min-h-dvh bg-darkbg">
            <div className="relative w-full max-w-[450px] mx-auto min-h-dvh max-h-dvh flex flex-col gap-2 pb-[72px] overflow-x-hidden">
                <Overlay active={menuOpen} close={() => setMenuOpen(false)} />
                <MobileMenu active={menuOpen} />
                <Header menuOpen={menuOpen} close={() => setMenuOpen(!menuOpen)} />

                <div className="px-4 pt-4 flex flex-col gap-2">
                    <div className="flex items-center gap-2 p-1 rounded-2xl bg-black/20 border border-primary/10">
                        <button
                            onClick={() => setActiveTab("athletes")}
                            className={`flex-1 py-3 rounded-xl text-sm font-bold transition-all duration-300 ${activeTab === "athletes"
                                ? "bg-primary text-black shadow-lg shadow-primary/20"
                                : "text-white/60 hover:text-white"
                                }`}
                        >
                            ورزشکاران
                        </button>
                        <button
                            onClick={() => setActiveTab("coaches")}
                            className={`flex-1 py-3 rounded-xl text-sm font-bold transition-all duration-300 ${activeTab === "coaches"
                                ? "bg-primary text-black shadow-lg shadow-primary/20"
                                : "text-white/60 hover:text-white"
                                }`}
                        >
                            مربی‌ها
                        </button>
                    </div>

                    <div className="relative">
                        <button
                            onClick={() => setDropdownOpen((p) => !p)}
                            className="w-full flex items-center justify-between px-4 py-3 rounded-2xl bg-black/20 border
                             border-primary/10 text-white text-sm font-bold transition-all"
                        >
                            <span>
                                ترتیب بر اساس:{" "}
                                <span className="text-primary">{activeSortLabel}</span>
                            </span>
                            <ChevronDown
                                size={16}
                                className={`text-primary transition-transform duration-300 ${dropdownOpen ? "rotate-180" : ""
                                    }`}
                            />
                        </button>

                        {dropdownOpen && (
                            <div className="absolute top-full mt-1 left-0 right-0 z-20 rounded-2xl bg-darkbg border border-primary/10 overflow-hidden shadow-xl">
                                {sortOptions.map((opt) => (
                                    <button
                                        key={opt.value}
                                        onClick={() => {
                                            setSortBy(opt.value);
                                            setDropdownOpen(false);
                                        }}
                                        className={`w-full px-4 py-3 text-sm text-right transition-colors ${sortBy === opt.value
                                            ? "bg-primary/15 text-primary font-bold"
                                            : "text-white/70 hover:bg-white/5"
                                            }`}
                                    >
                                        {opt.label}
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>
                </div>

                <main className="flex-1 overflow-y-auto no-scrollbar p-4">
                    <div className="h-full rounded-[24px] bg-black/20 border border-primary/10 p-3 overflow-hidden">
                        <div className="flex items-center justify-end mb-3 px-1">
                            {currentList && (
                                <p className="text-white/40 text-xs">{currentList.length} نفر</p>
                            )}
                        </div>

                        <div className="overflow-y-auto no-scrollbar grid grid-cols-2 gap-3 pl-1">
                            {currentList.length > 0 ? (
                                currentList.map((user, index) => (
                                    <LeaderboardExpandableRow
                                        key={user.id}
                                        user={user}
                                        rank={index + 1}
                                        sortBy={sortBy}
                                        onClick={() => navigate(`/profile/${user.id}`)}
                                    />
                                ))
                            ) : (
                                <div className="col-span-2 h-full flex items-center justify-center text-white/50 text-sm">
                                    موردی برای نمایش وجود ندارد
                                </div>
                            )}
                        </div>
                    </div>
                </main>

                <Footer activeTab={activeTabFooter} setActiveTab={setActiveTabFooter} />
            </div>
        </div>
    );
};

type LeaderboardExpandableRowProps = {
    user: User;
    rank: number;
    sortBy: SortKey;
    onClick: () => void;
};

const statConfig: Record<SortKey, { label: string; suffix: string }> = {
    program_count: { label: "برنامه", suffix: "" },
    distance_km: { label: "کیلومتر", suffix: "" },
    elevation_m: { label: "متر", suffix: "" },
    badge_count: { label: "مدال", suffix: "" },
};

const LeaderboardExpandableRow = ({
    user,
    rank,
    sortBy,
    onClick,
}: LeaderboardExpandableRowProps) => {
    const rankColors = [
        "bg-yellow-400 text-black",
        "bg-slate-300 text-black",
        "bg-amber-700 text-white",
    ];
    const rankClass =
        rank <= 3 ? rankColors[rank - 1] : "bg-white/10 text-white/80";

    const statValue = parseFloat(String(user[sortBy] ?? 0));
    const { label } = statConfig[sortBy];

    return (
        <div className="h-[220px] max-h-fit min-h-fit rounded-2xl overflow-hidden border border-white/5 bg-white/5">
            <button onClick={onClick} className="relative w-full p-3 text-right">
                <div className="absolute left-3 w-10 h-10 bg-black min-h-fit min-w-fit rounded-2xl">
                    <div className="w-full h-full rounded-2xl bg-primary/15 flex flex-col items-center justify-center">
                        <span className="text-primary font-bold text-sm">
                            {Number.isInteger(statValue) ? statValue : statValue.toFixed(1)}
                        </span>
                        <span className="text-white/45 text-[9px]">{label}</span>
                    </div>
                </div>

                <div className="h-full max-h-44 aspect-square p-1 rounded-2xl overflow-hidden flex items-center justify-center">
                    <img
                        src={user.avatar || "../assets/img/avatar.png"}
                        alt={user.full_name}
                        className="w-full h-full object-cover rounded-xl"
                    />
                </div>

                <div className="flex items-center gap-3 min-w-0 mt-1">
                    <div
                        className={`w-10 h-10 rounded-full flex items-center justify-center font-bold text-sm shrink-0 ${rankClass}`}
                    >
                        {rank}
                    </div>
                    <div className="min-w-0">
                        <h3 className="text-white text-sm font-bold truncate">
                            {user.full_name}
                        </h3>
                        <p className="text-white/50 text-xs truncate">
                            {user.job_title} • سطح {user.level}
                        </p>
                    </div>
                </div>
            </button>
        </div>
    );
};

export default LeaderboardPage;